import { Input } from "@component/input";
import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { Checkbox } from "@component/checkbox";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@component/command";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select"
import { MailIcon, XIcon, UploadIcon, ChevronDownIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { WorkspaceCompanyRequest } from "@type/candidate";
import { createWorkspaceCompany, editWorkspaceCompany, getDetailWorkspaceCompany } from "@service/employer";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { Assessment, FormAssessment, InterviewAssessment } from "@type/assessments";
import { getAdminListAssessment } from "@service/assessment";
import { getListJob } from "@service/job";
import { Job } from "@type/job";
import { isOuraEnabled } from "@oura/role";

/** The workspace products Super Admin can provision. Named so the state and the
 *  option list share one definition. */
type WorkspaceProductType = "recruiter" | "operator" | "trainer" | "oura";

const WORKSPACE_PRODUCTS: { value: WorkspaceProductType; label: string }[] = [
    { value: "recruiter", label: "Recruiter" },
    { value: "operator", label: "Operator" },
    { value: "trainer", label: "Trainer" },
    { value: "oura", label: "Oura" },
];

interface Props {
    isEditMode?: boolean;
}

export default function WorkspaceCompanyForm({ isEditMode = false }: Props) {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { company_id } = useParams();
    const [formData, setFormData] = useState<WorkspaceCompanyRequest>({
        first_name: "",
        last_name: "",
        email: "",
        company_logo: null,
        company_name: "",
        workspace_url: "",
        workspace_access_type: "full",
        workspace_access_duration: 9999,
        mock_job_id: "",
        mock_assessment_id: "",
        mock_form_id: "",
    });

    // Track workspace status, date created, and expiry date for edit mode
    const [workspaceStatus, setWorkspaceStatus] = useState<string | null>(null);
    const [expiryDate, setExpiryDate] = useState<Date | null>(null);
    const [duplicateJobs, setDuplicateJobs] = useState<string[]>([]);
    const [duplicateAIInterviews, setDuplicateAIInterviews] = useState<string[]>([]);
    const [duplicateForms, setDuplicateForms] = useState<string[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isLoadingDetail, setLoadingDetail] = useState(false);
    const [workspaceType, setWorkspaceType] = useState<"full" | "temporary">("full");
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [openJobsPopover, setOpenJobsPopover] = useState(false);
    const [openAIInterviewsPopover, setOpenAIInterviewsPopover] = useState(false);
    const [openFormsPopover, setOpenFormsPopover] = useState(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [jobs, setJobs] = useState<Job[]>([]);
    const [isLoadingJob, setLoadingJob] = useState<boolean>(true);
    const [isLoadingAssessment, setLoadingAssessment] = useState<boolean>(true);
    const [productType, setProductType] = useState<WorkspaceProductType>("recruiter");

    useEffect(() => {
        getAdminListAssessment().then((resp) => {
            if (resp.status === 200) {
                setAssessments(resp.data.data);
            }
        }).then(() => {
            setLoadingAssessment(false);
        })
        getListJob().then((resp) => {
            if (resp.status === 200) {
                setJobs(resp.data.data);
            }
        }).finally(() => {
            setLoadingJob(false);
        })
    }, [])

    useEffect(() => {
        if (company_id && isEditMode) {
            setLoadingDetail(true);
            getDetailWorkspaceCompany(company_id).then((resp) => {
                if (resp.status === 200) {
                    setFormData({
                        first_name: resp.data.data.first_name,
                        last_name: resp.data.data.last_name,
                        email: resp.data.data.email,
                        company_name: resp.data.data.company_name,
                        workspace_url: resp.data.data.subdomain,
                        workspace_access_type: resp.data.data.workspace_access_duration === 9999 ? "full" : "temporary",
                        workspace_access_duration: resp.data.data.workspace_access_duration,
                    })
                    setLogoPreview(resp.data.data.company_logo);
                    setDuplicateAIInterviews(resp.data.data.assessment_id || []);
                    setDuplicateJobs(resp.data.data.job_ids || []);
                    setDuplicateForms(resp.data.data.form_ids || []);
                    setProductType(resp.data.data.workspace_product_type || "recruiter");
                }
            }).finally(() => {
                setLoadingDetail(false);
            });
        }
    }, [company_id])

    // Link expiry is always 7 days from today (rolling window)
    const linkExpiryDays = 7;

    // Calculate countdown and format expiry info
    const getExpiryInfo = () => {
        // If in edit mode and link is expired, access expired, or deactivated, show only the status
        if (isEditMode && (workspaceStatus === "Link Expired" || workspaceStatus === "Access Expired" || workspaceStatus === "Link Deactivated" || workspaceStatus === "Access Deactivated")) {
            return workspaceStatus;
        }

        // Always calculate expiry date as 7 days from today (rolling window)
        const today = new Date();
        const targetExpiryDate = new Date(today);
        targetExpiryDate.setDate(today.getDate() + linkExpiryDays);

        // Format the date
        const formattedDate = targetExpiryDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        // Always show 7 days since it's always 7 days from today
        return `${formattedDate} (${linkExpiryDays} days)`;
    };

    // Auto-generate workspace URL from company name
    const generateWorkspaceUrl = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
    };

    const handleCompanyNameChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            company_name: value,
            workspace_url: generateWorkspaceUrl(value),
        }));
    };

    const toggleSelection = (type: 'jobs' | 'aiInterviews' | 'forms', id: string) => {
        if (type === 'jobs') {
            setDuplicateJobs((prev) => [...prev, id]);
        } else if (type === 'aiInterviews') {
            setDuplicateAIInterviews((prev) => [...prev, id]);
        } else if (type === 'forms') {
            setDuplicateForms((prev) => [...prev, id]);
        }
    };

    const removeItem = (type: 'jobs' | 'aiInterviews' | 'forms', id: string) => {
        if (type === 'jobs') {
            setDuplicateJobs((prev) => prev.filter((item) => item !== id));
        } else if (type === 'aiInterviews') {
            setDuplicateAIInterviews((prev) => prev.filter((item) => item !== id));
        } else if (type === 'forms') {
            setDuplicateForms((prev) => prev.filter((item) => item !== id));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true);
        if (isEditMode) {
            editWorkspaceCompany(
                company_id as string,
                {
                    ...formData,
                    workspace_access_type: workspaceType,
                    workspace_product_type: productType,
                    mock_job_id: productType === "recruiter" ? duplicateJobs.join(",") : "",
                    mock_assessment_id: productType === "recruiter" ? duplicateAIInterviews.join(",") : "",
                    mock_form_id: productType === "recruiter" ? duplicateForms.join(",") : "",
                    workspace_access_duration: workspaceType === "full" ? 9999 : formData.workspace_access_duration,
                }).then((resp) => {
                    if (resp.status === 200) {
                        snackbar.showSuccess("Workspace company updated successfully.");
                    }
                }).catch((error) => {
                    snackbar.showError("Failed to update workspace company. Please try again.");
                    catchErrorLog(`failed to update workspace company: ${error}`)
                }).finally(() => {
                    setLoading(false);
                })
            return;
        }

        createWorkspaceCompany({
            ...formData,
            workspace_access_type: workspaceType,
            workspace_product_type: productType,
            mock_job_id: productType === "recruiter" ? duplicateJobs.join(",") : "",
            mock_assessment_id: productType === "recruiter" ? duplicateAIInterviews.join(",") : "",
            mock_form_id: productType === "recruiter" ? duplicateForms.join(",") : "",
            workspace_access_duration: workspaceType === "full" ? 9999 : formData.workspace_access_duration,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Workspace company created successfully.");
                navigate(`/workspaces`)
            }
        }).catch((error) => {
            snackbar.showError(error?.response?.data?.message || "Failed to create workspace company. Please try again.");
            catchErrorLog(`failed to create workspace company: ${error}`)
        }).finally(() => {
            setLoading(false);
        })
    };

    const handleLogoUpload = (file: File) => {
        if (
            file &&
            (file.type.includes("image/png") ||
                file.type.includes("image/svg") ||
                file.type.includes("image/jpeg") ||
                file.type.includes("image/jpg"))
        ) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            setFormData((prev) => ({
                ...prev,
                company_logo: file,
            }));
        }
    };

    const removeLogo = () => {
        setFormData((prev) => ({
            ...prev,
            company_logo: null,
        }));
        setLogoPreview(null);
    };

    const handleReactivateLink = () => {
        // Here you would typically send a request to your backend to reactivate the link
        console.log("Reactivating link for workspace");
    };

    const MultiSelectDropdown = ({
        label,
        placeholder,
        items,
        selectedIds,
        type,
        open,
        setOpen,
    }: {
        label: string;
        placeholder: string;
        items: { id: string; name: string }[];
        selectedIds: string[];
        type: 'jobs' | 'aiInterviews' | 'forms';
        open: boolean;
        setOpen: (open: boolean) => void;
    }) => {
        const selectedItems = items.filter((item) => selectedIds.includes(item.id as string));

        return (
            <div className="space-y-2">
                <Label className="text-sm font-medium">{label}</Label>
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full justify-between h-auto min-h-[48px] px-3 py-2"
                        >
                            <div className="flex flex-wrap gap-1.5 flex-1">
                                {selectedItems.length === 0 ? (
                                    <span className="text-gray-500">{placeholder}</span>
                                ) : (
                                    selectedItems.map((item) => (
                                        <span
                                            key={item.id}
                                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm"
                                        >
                                            {item.name}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeItem(type, item.id);
                                                }}
                                                className="hover:bg-blue-200 rounded-full p-0.5"
                                            >
                                                <XIcon className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))
                                )}
                            </div>
                            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                            <CommandInput placeholder={label === "AI Interviews" ? "Search AI interviews..." : `Search ${label.toLowerCase()}...`} />
                            <CommandEmpty>No items found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                                {items.map((item, index) => (
                                    <CommandItem
                                        key={index}
                                        onSelect={() => toggleSelection(type, item.id)}
                                        className="cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2 w-full">
                                            <Checkbox
                                                checked={selectedIds.includes(item.id)}
                                                onCheckedChange={() => toggleSelection(type, item.id)}
                                            />
                                            <span>{item.name}</span>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </Command>
                    </PopoverContent>
                </Popover>
                {selectedItems.length > 0 && (
                    <p className="text-xs text-gray-500">
                        {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white">
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Company Information */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Company Information
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label
                                htmlFor="companyName"
                                className="text-sm font-medium"
                            >
                                Company Name
                            </Label>
                            <Input
                                id="companyName"
                                value={formData.company_name}
                                onChange={(e) => handleCompanyNameChange(e.target.value)}
                                placeholder="ourteam"
                                required
                                className="h-12"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="workspaceUrl"
                                className="text-sm font-medium"
                            >
                                Workspace URL
                            </Label>
                            <div className="flex items-center">
                                <div className="bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 h-12 flex items-center text-sm text-gray-600 whitespace-nowrap">
                                    https://
                                </div>
                                <Input
                                    id="workspaceUrl"
                                    value={formData.workspace_url}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            workspace_url: e.target.value,
                                        }))
                                    }
                                    placeholder="e.g., ourteam-solutions"
                                    className={`h-12 rounded-l-none border-l-0 focus:border-l ${isEditMode ? "bg-gray-50 cursor-not-allowed" : ""}`}
                                    disabled={isEditMode}
                                />

                                <div className="bg-gray-100 border border-l-0 border-gray-300 rounded-r-md px-3 h-12 flex items-center text-sm text-gray-600 whitespace-nowrap">
                                    .ourteam.ai
                                </div>
                            </div>

                            <p className="text-xs text-gray-500">
                                {isEditMode ? "Workspace URL cannot be changed after creation." : "Auto-generated from company name if left empty (e.g., ourteam-solutions)."}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="linkExpiry"
                                className="text-sm font-medium"
                            >
                                Activation Link Expiry
                            </Label>
                            <div className="relative">
                                <Input
                                    id="linkExpiry"
                                    type="text"
                                    value={getExpiryInfo()}
                                    readOnly
                                    className="h-12 bg-gray-50 cursor-not-allowed pr-40"
                                />
                                {isEditMode && (workspaceStatus === "Link Deactivated" || workspaceStatus === "Link Expired") && (
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <button
                                            type="button"
                                            onClick={handleReactivateLink}
                                            className="flex items-center gap-1.5 text-blue-600 underline hover:text-black transition-colors text-sm group"
                                        >
                                            <MailIcon className="h-4 w-4 text-blue-600 group-hover:text-black transition-colors" />
                                            Reactivate Link
                                        </button>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-gray-500">
                                Activation links expire after 7 days from today.
                            </p>
                        </div>
                    </div>

                    {/* Company Logo and Workspace Access Type - 2 Column Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Company Logo */}
                        <div className="space-y-2">
                            <Label htmlFor="companyLogo" className="text-sm font-medium">
                                Company Logo
                            </Label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
                                {logoPreview ? (
                                    <div className="relative">
                                        <img
                                            src={(isEditMode) ? `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${logoPreview}` : logoPreview}
                                            alt="Company logo preview"
                                            className="w-full h-32 object-contain rounded"
                                        />
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            size="sm"
                                            className="absolute top-2 right-2 h-8 w-8 p-0"
                                            onClick={removeLogo}
                                        >
                                            <XIcon className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <label
                                        htmlFor="companyLogo"
                                        className="flex flex-col items-center justify-center h-32 cursor-pointer"
                                    >
                                        <UploadIcon className="h-12 w-12 text-gray-400 mb-3" />
                                        <span className="text-sm font-medium text-gray-700 mb-1">
                                            Upload PNG, SVG, or JPG
                                        </span>
                                        <span className="text-xs text-gray-500">Max 5MB</span>
                                    </label>
                                )}
                                <input
                                    id="companyLogo"
                                    type="file"
                                    accept=".png,.svg,.jpg,.jpeg"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleLogoUpload(file);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Workspace Access Type */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">
                                    Workspace Access Type
                                </Label>
                                {isEditMode && (workspaceStatus === "Access Expired" || workspaceStatus === "Access Deactivated") && (
                                    <button
                                        type="button"
                                        onClick={handleReactivateLink}
                                        className="flex items-center gap-1.5 text-blue-600 underline hover:text-black transition-colors text-sm group"
                                    >
                                        <MailIcon className="h-4 w-4 text-blue-600 group-hover:text-black transition-colors" />
                                        Reactivate Access
                                    </button>
                                )}
                            </div>
                            <RadioGroup
                                value={workspaceType}
                                onValueChange={(value: "full" | "temporary") => {
                                    setWorkspaceType(value);
                                    if (value === "temporary") {
                                        setFormData((prev) => ({
                                            ...prev,
                                            workspace_access_duration: 7,
                                        }));
                                    }
                                }}
                                className="space-y-3"
                            >
                                {/* Full Access Option */}
                                <div className="flex items-start gap-3">
                                    <RadioGroupItem value="full" id="access-full" className="mt-1" />
                                    <div className="flex-1">
                                        <label
                                            htmlFor="access-full"
                                            className="text-sm font-medium text-gray-900 cursor-pointer"
                                        >
                                            Full Access
                                        </label>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Users can constantly access the workspace after activation.
                                        </p>
                                    </div>
                                </div>

                                {/* Temporary Access Option */}
                                <div className="flex items-start gap-3">
                                    <RadioGroupItem value="temporary" id="access-temporary" className="mt-1" />
                                    <div className="flex-1">
                                        <label
                                            htmlFor="access-temporary"
                                            className="text-sm font-medium text-gray-900 cursor-pointer"
                                        >
                                            Temporary Access
                                        </label>
                                        <p className="text-xs text-gray-500 mt-0.5 mb-3">
                                            Workspace access expires after a specified period from activation.
                                        </p>

                                        {/* Temporary Access Duration Dropdown */}
                                        {workspaceType === "temporary" && (
                                            <div className="space-y-2">
                                                <Label htmlFor="access-duration" className="text-sm font-medium text-gray-700">
                                                    Access Duration
                                                </Label>
                                                <Select
                                                    value={formData.workspace_access_duration.toString()}
                                                    onValueChange={(value) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            workspace_access_duration: parseInt(value),
                                                        }))
                                                    }
                                                >
                                                    <SelectTrigger id="access-duration" className="h-12">
                                                        <SelectValue placeholder="Select duration" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="7">7 days</SelectItem>
                                                        <SelectItem value="14">14 days</SelectItem>
                                                        <SelectItem value="30">30 days</SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                {/* Access Expiry Date - Only shown in edit mode for Activated workspaces with temporary access */}
                                                {isEditMode && workspaceStatus === "Activated" && workspaceType === "temporary" && (
                                                    <div className="space-y-2 mt-3">
                                                        <Label htmlFor="access-expiry" className="text-sm font-medium text-gray-700">
                                                            Access Expiry
                                                        </Label>
                                                        <div className="relative">
                                                            <Input
                                                                id="access-expiry"
                                                                type="text"
                                                                value={expiryDate ? expiryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "–"}
                                                                readOnly
                                                                className="h-12 bg-gray-50 cursor-not-allowed"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </RadioGroup>
                        </div>
                    </div>
                </div>

                {/* Workspace Product Type */}
                <div className="space-y-4">
                    <div className="space-y-1">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Workspace Product
                        </h2>
                        <p className="text-sm text-gray-600">
                            Select the product this workspace will be configured for.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Oura is dropped while the product is switched off in
                            production: offering the type here would let an admin
                            create a workspace nobody can sign into. */}
                        {WORKSPACE_PRODUCTS.filter(
                            (p) => p.value !== "oura" || isOuraEnabled(),
                        ).map(({ value, label }) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setProductType(value)}
                                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                                    productType === value
                                        ? "border-blue-600 bg-blue-50"
                                        : "border-gray-200 hover:border-gray-300 bg-white"
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                        productType === value ? "border-blue-600" : "border-gray-400"
                                    }`}>
                                        {productType === value && (
                                            <div className="w-2 h-2 rounded-full bg-blue-600" />
                                        )}
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900">{label}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Duplicate Workspace Content — only relevant for Recruiter */}
                {productType === "recruiter" && (
                <div className="space-y-4">
                    <div className="space-y-1">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Duplicate Workspace Content
                        </h2>
                        <p className="text-sm text-gray-600">
                            Select existing content to duplicate into this workspace.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {!isLoadingDetail && !isLoadingJob && (
                            <MultiSelectDropdown
                                label="Jobs"
                                placeholder="Select jobs to duplicate"
                                items={jobs.map((job) => ({
                                    id: job.id,
                                    name: `[${job.company_name}] ${job.job_title}`,
                                }))}
                                selectedIds={duplicateJobs}
                                type="jobs"
                                open={openJobsPopover}
                                setOpen={setOpenJobsPopover}
                            />
                        )}
                        {!isLoadingDetail && !isLoadingAssessment && (
                            <MultiSelectDropdown
                                label="AI Interviews"
                                placeholder="Select AI interviews to duplicate"
                                items={
                                    (assessments.filter((item) => item.type === "Interview" && !!item.id) || [])
                                        .map((item) => ({
                                            id: item.id as string,
                                            name: `${(item as InterviewAssessment).content.aiName} - ${(item as InterviewAssessment).content.interviewName}`,
                                        })) || []
                                }
                                selectedIds={duplicateAIInterviews}
                                type="aiInterviews"
                                open={openAIInterviewsPopover}
                                setOpen={setOpenAIInterviewsPopover}
                            />
                        )}
                        {!isLoadingDetail && !isLoadingAssessment && (
                            <MultiSelectDropdown
                                label="Forms"
                                placeholder="Select forms to duplicate"
                                items={
                                    (assessments.filter((item) => item.type === "Form" && !!item.id) || [])
                                        .map((item) => ({
                                            id: item.id as string,
                                            name: (item as FormAssessment).content.title,
                                        })) || []
                                }
                                selectedIds={duplicateForms}
                                type="forms"
                                open={openFormsPopover}
                                setOpen={setOpenFormsPopover}
                            />
                        )}
                    </div>
                </div>
                )}

                {/* Workspace Owner Details */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Workspace Owner Details
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label
                                htmlFor="ownerFirstName"
                                className="text-sm font-medium"
                            >
                                First Name
                            </Label>
                            <Input
                                id="ownerFirstName"
                                type="text"
                                value={formData.first_name}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        first_name: e.target.value,
                                    }))
                                }
                                placeholder="e.g., John"
                                required
                                className="h-12"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="ownerLastName"
                                className="text-sm font-medium"
                            >
                                Last Name
                            </Label>
                            <Input
                                id="ownerLastName"
                                type="text"
                                value={formData.last_name}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        last_name: e.target.value,
                                    }))
                                }
                                placeholder="e.g., Smith"
                                required
                                className="h-12"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label
                                htmlFor="ownerEmail"
                                className="text-sm font-medium"
                            >
                                Email Address
                            </Label>
                            <Input
                                id="ownerEmail"
                                type="email"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }))
                                }
                                placeholder="e.g., owner@ourteam.ai"
                                required
                                className="h-12"
                            />
                            <p className="text-xs text-gray-500">
                                Activation email will be sent to this address.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                {!isEditMode && (
                    <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                        <p className="text-sm text-gray-600 max-w-2xl">
                            Creating the workspace automatically sends a unique activation link to the recipient.
                        </p>
                        <div className="flex items-center gap-4">
                            <Link to="/workspaces">
                                <Button type="button" variant="outline" className="h-10 px-4">
                                    Cancel
                                </Button>
                            </Link>
                            <Button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap h-10 px-4"
                                disabled={!formData.company_name || !formData.email || !formData.first_name || !formData.last_name || isLoading}
                            >
                                Create Workspace
                            </Button>
                        </div>
                    </div>
                )}
                {isEditMode && (
                    <div className="flex items-center justify-end pt-6 border-t border-gray-200">
                        <div className="flex items-center gap-4">
                            <Link to="/workspaces">
                                <Button type="button" variant="outline" className="h-10 px-4">
                                    Cancel
                                </Button>
                            </Link>
                            <Button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap h-10 px-4"
                                disabled={!formData.company_name || !formData.email || !formData.first_name || !formData.last_name || isLoading}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    )
}